import type { PoolCalculateResponse, PoolPlacement, PoolApplication } from '@/api/calculators/types';
import { fmt, rs } from './constants';
import { SummaryTile, StockPill, KvRow, HeatUpRow, BuiltInFeaturesPanel, EngineeringNotesPanel } from './PoolUi';

export function ForwardResultsTabs({
  subTab,
  setSubTab,
}: {
  subTab: 'rec' | 'roi';
  setSubTab: (v: 'rec' | 'roi') => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Forward flow result pages"
      className="mb-3 flex gap-1 rounded-lg bg-stone-200 p-1 print:hidden"
    >
      <button
        type="button"
        role="tab"
        aria-selected={subTab === 'rec'}
        data-testid="pool-fwd-tab-rec"
        onClick={() => setSubTab('rec')}
        className={`flex-1 rounded-md px-3 py-2 text-center text-sm transition ${
          subTab === 'rec'
            ? 'bg-white font-bold text-slate-900 shadow-sm'
            : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        <div>Page 1 · Recommendation</div>
        <div className="mt-0.5 text-[11px] font-normal text-slate-500">
          Sizing &amp; configuration
        </div>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={subTab === 'roi'}
        data-testid="pool-fwd-tab-roi"
        onClick={() => setSubTab('roi')}
        className={`flex-1 rounded-md px-3 py-2 text-center text-sm transition ${
          subTab === 'roi'
            ? 'bg-white font-bold text-slate-900 shadow-sm'
            : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        <div>Page 2 · ROI Analysis</div>
        <div className="mt-0.5 text-[11px] font-normal text-slate-500">
          Auto-attached to commercial quotes
        </div>
      </button>
    </div>
  );
}

// ─── Recommendation view (Page 1) ───────────────────────────────────────────

export function RecommendationView({
  response,
  placement,
  application,
  targetTemp,
  heatTimeTarget,
  winterOp,
}: {
  response: PoolCalculateResponse;
  placement: PoolPlacement;
  application: PoolApplication;
  targetTemp: number;
  heatTimeTarget: number;
  winterOp: 'yes' | 'no';
}) {
  const { sizing, roi_full } = response;
  const opt1 = sizing.recommendation.option_1_no_redundancy;
  const opt2 = sizing.recommendation.option_2_with_redundancy;
  const upgraded = !!opt2.model_upgraded_from_option_1;

  return (
    <div data-testid="pool-rec-view" className="space-y-4">
      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-2">
        <SummaryTile
          label="Volume"
          value={`${fmt(sizing.pool.volume_m3, 1)} m³`}
        />
        <SummaryTile
          label="Heat load"
          value={`${fmt(sizing.load.required_capacity_kw, 1)} kW`}
        />
        <SummaryTile
          label="@ design"
          value={`${sizing.design_conditions.design_ambient_c}°C`}
        />
      </div>

      {sizing.recommendation.upgrade_reason && (
        <div
          data-testid="pool-upgrade-warning"
          className="rounded-md border-l-4 border-amber-600 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <strong>↑ Model upgraded.</strong>{' '}
          {sizing.recommendation.upgrade_reason}
        </div>
      )}

      {/* Two option cards */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* Option 1 — standard slate border */}
        <div
          data-testid="pool-option-1"
          className="relative rounded-lg border border-slate-200 bg-white p-4"
        >
          <span className="absolute right-2 top-2 rounded bg-rose-50 px-1.5 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-wider text-rose-800">
            No Redundancy
          </span>
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Option 1 · Standard
          </div>
          <div className="mt-1 text-lg font-bold leading-tight">
            {opt1.qty_working === 1
              ? '1 Machine'
              : `${opt1.qty_working} Machines`}
          </div>
          <div className="text-xs text-slate-600">
            {opt1.qty_working} × {opt1.model_id}
          </div>
          <StockPill inStock={opt1.in_stock} />
          <div className="mt-2 grid grid-cols-2 gap-1 text-[11px]">
            <KvRow label="Output" value={`${fmt(opt1.total_output_kw, 1)} kW`} />
            <KvRow label="COP" value={fmt(opt1.cop_at_ambient, 2)} />
          </div>
          <HeatUpRow heatUp={opt1.estimated_heat_up} />
        </div>

        {/* Option 2 — emerald border 2px + "RECOMMENDED" badge if upgraded */}
        <div
          data-testid="pool-option-2"
          className="relative rounded-lg border-2 border-emerald-600 bg-gradient-to-b from-emerald-50 to-white p-4"
        >
          <span
            className={`absolute right-2 top-2 rounded px-1.5 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-wider ${
              upgraded
                ? 'bg-amber-300 text-amber-900'
                : 'bg-emerald-100 text-emerald-800'
            }`}
            data-testid={upgraded ? 'pool-option-2-upgraded-badge' : undefined}
          >
            {upgraded ? 'Recommended · Upgraded' : 'Dual Mode'}
          </span>
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">
            Option 2 · Variable Load
          </div>
          {upgraded ? (
            <>
              <div className="mt-1 text-lg font-bold leading-tight">
                2 × {opt2.model_id}
              </div>
              <div className="text-xs text-slate-600">
                Mutual Redundancy — either machine alone covers full load
              </div>
              <StockPill inStock={opt2.in_stock} />
              <div className="mt-2 grid grid-cols-2 gap-1 text-[11px]">
                <KvRow
                  label="Total cap"
                  value={`${fmt(opt2.total_output_kw, 1)} kW`}
                />
                <KvRow
                  label="Peak (both)"
                  value={`${fmt(opt2.total_output_kw, 1)} kW`}
                />
              </div>
              <HeatUpRow heatUp={opt2.estimated_heat_up} />
            </>
          ) : (
            <>
              <div className="mt-1 text-lg font-bold leading-tight">
                {opt2.qty_total} Machines · Peak Boost + Standby
              </div>
              <div className="text-xs text-slate-600">
                {opt2.qty_working}W + {opt2.qty_standby}S × {opt2.model_id}
              </div>
              <StockPill inStock={opt2.in_stock} />
              <div className="mt-3 rounded-md border-l-[3px] border-emerald-600 bg-white/70 px-2.5 py-2">
                <div className="font-mono text-[10px] font-bold uppercase text-emerald-700">
                  ⚡ Peak Boost · {opt2.qty_total} parallel
                </div>
                <div className="mt-1 flex justify-between text-[11.5px]">
                  <span>
                    {fmt(
                      opt2.capacity_per_unit_at_ambient_kw * opt2.qty_total,
                      1,
                    )}{' '}
                    kW (peak)
                  </span>
                  <span className="font-mono font-bold">
                    {Math.ceil(opt2.estimated_heat_up.hours_low / 2)}–
                    {Math.ceil(opt2.estimated_heat_up.hours_high / 2)} hr
                  </span>
                </div>
              </div>
              <div className="mt-2 rounded-md border-l-[3px] border-blue-700 bg-white/70 px-2.5 py-2">
                <div className="font-mono text-[10px] font-bold uppercase text-blue-700">
                  ⟳ Normal · {opt2.qty_working}W + {opt2.qty_standby}S alternating
                </div>
                <div className="mt-1 flex justify-between text-[11.5px]">
                  <span>{fmt(opt2.total_output_kw, 1)} kW</span>
                  <span className="font-mono font-bold">
                    {opt2.estimated_heat_up.hours_low}–
                    {opt2.estimated_heat_up.hours_high} hr
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sales narrative callout */}
      <div className="rounded-lg border border-stone-200 bg-gradient-to-br from-emerald-50 to-blue-50 p-3">
        <div className="font-mono text-[10.5px] font-bold uppercase tracking-wide text-slate-700">
          {upgraded ? 'Why Option 2 upgrades the model' : 'Why pick Option 2?'}
        </div>
        <div className="mt-1 text-xs leading-relaxed text-slate-700">
          {upgraded ? (
            <>
              Instead of {opt1.qty_working + 1} × {opt1.model_id} (
              {opt1.qty_working} working + 1 standby ={' '}
              {opt1.qty_working + 1} machines), Option 2 uses 2 × {opt2.model_id}.
              Same peak capacity, half the machines, fewer pumps, simpler cascade.
              Either machine alone covers the full load — when one is in service,
              the other carries the pool.
            </>
          ) : (
            <>
              Option 2 gives <strong>{opt2.qty_total}× peak capacity on demand</strong>{' '}
              — run all units in parallel for fast pre-opening heat-up, big party
              refill, or unexpected cold snap. Cascade panel auto-alternates them
              in normal operation for even wear and fault coverage.
            </>
          )}
        </div>
      </div>

      {/* Cooling-bonus chip (when present) */}
      {roi_full.cooling_bonus_applies && roi_full.cooling_bonus_per_year > 0 && (
        <div
          data-testid="pool-cooling-bonus"
          className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-900"
        >
          Cooling savings: {rs(roi_full.cooling_bonus_per_year)}/yr (outdoor pool, warm climate)
        </div>
      )}

      {/* Page 2 nudge */}
      <div className="rounded-md border-l-[3px] border-blue-700 bg-blue-50 px-3 py-2 text-xs leading-relaxed text-blue-900 print:hidden">
        → <strong>Page 2 · ROI Analysis</strong> auto-attached to this commercial
        quotation. Tap the tab above to view savings vs LPG, Diesel, and Electric.
      </div>

      {/* Built-in features */}
      <BuiltInFeaturesPanel features={sizing.builtin_features} />

      {/* Engineering notes accordion */}
      <EngineeringNotesPanel notes={sizing.engineering_notes} />

      {/* Tiny context strip */}
      <div className="pt-1 text-[11px] text-slate-500">
        {sizing.pool.input_mode === 'volume' ? `Volume entry · estimated surface area ${fmt(sizing.pool.surface_area_m2, 2)} m² · assumed depth ${sizing.pool.assumed_depth_m} m` : `${sizing.pool.length_m}×${sizing.pool.width_m}×${sizing.pool.depth_m} m`} ·{' '}
        {sizing.design_conditions.climate_name} · {placement} · {application} ·
        target {targetTemp}°C · heat-up ≤ {heatTimeTarget} hr
        {winterOp === 'yes' ? ' · year-round' : ''}
      </div>
    </div>
  );
}

// ─── ROI page (Page 2 — auto-attached) ──────────────────────────────────────
